import { lazy, type ComponentType } from 'react'

export type DisplayType =
  | 'story_block'
  | 'carousel'
  | 'grid'
  | 'hero'
  | 'circle_stories'
  | 'floating_player'
  | 'player_deck'
  | 'horizontal_player'
  | 'horizontal_carousel'
  | 'dynamic_carousel'
  | 'vertical_stories'

export interface SurfaceDisplayProps {
  organization: string
  apiUrl: string
  surface: string
  viewerId?: string
  punterId?: string
  offering?: string
  seeMoreUrl?: string
}

type DisplayComponent = ComponentType<SurfaceDisplayProps>

// The registry's contract is stable: every display_type maps to the real
// runtime component used by the embedded widget and Studio preview harness.
export const SURFACE_REGISTRY: Record<DisplayType, DisplayComponent> = {
  story_block: lazy(async () => {
    const mod = await import('../components/ThemedFeed')
    return { default: mod.ThemedFeed as React.ComponentType<SurfaceDisplayProps> }
  }),
  carousel: lazy(async () => {
    const mod = await import('../components/carousel/CardCarousel')
    return { default: mod.CardCarousel as React.ComponentType<SurfaceDisplayProps> }
  }),
  grid: lazy(async () => {
    const mod = await import('./GridSurface')
    return { default: mod.default as React.ComponentType<SurfaceDisplayProps> }
  }),
  hero: lazy(async () => {
    const mod = await import('./HeroSurface')
    return { default: mod.default as React.ComponentType<SurfaceDisplayProps> }
  }),
  circle_stories: lazy(async () => {
    const mod = await import('./CircleStoriesSurface')
    return { default: mod.default as React.ComponentType<SurfaceDisplayProps> }
  }),
  floating_player: lazy(async () => {
    const mod = await import('./FloatingPlayerSurface')
    return { default: mod.default as React.ComponentType<SurfaceDisplayProps> }
  }),
  player_deck: lazy(async () => {
    const mod = await import('./PlayerDeckSurface')
    return { default: mod.default as React.ComponentType<SurfaceDisplayProps> }
  }),
  horizontal_player: lazy(async () => {
    const mod = await import('./HorizontalPlayerSurface')
    return { default: mod.default as React.ComponentType<SurfaceDisplayProps> }
  }),
  horizontal_carousel: lazy(async () => {
    const mod = await import('./HorizontalCarouselSurface')
    return { default: mod.default as React.ComponentType<SurfaceDisplayProps> }
  }),
  dynamic_carousel: lazy(async () => {
    const mod = await import('./DynamicCarouselSurface')
    return { default: mod.default as React.ComponentType<SurfaceDisplayProps> }
  }),
  vertical_stories: lazy(async () => {
    const mod = await import('./VerticalStoriesSurface')
    return { default: mod.default as React.ComponentType<SurfaceDisplayProps> }
  }),
}

export function isDisplayType(value: string | undefined | null): value is DisplayType {
  if (!value) return false
  return value in SURFACE_REGISTRY
}
